/**
 * Prefab expansion: turns a list of scene objects, with prefab placements in
 * it, into a tree of resolved nodes with runtime id paths and overrides
 * applied. The runtime builds from this tree, and the editor and `validate`
 * use it to report stale overrides and cycles.
 */
import { PLACEMENT_TRANSFORM_FIELDS } from './constants.js';
import { joinIdPath } from './ids.js';
import type { PrefabFile, PrefabOverrides, PrefabPlacement, SceneObject } from './types.js';

export type PrefabLookup = (path: string) => PrefabFile | undefined;

export interface ResolvedNode {
  /** Runtime id path from the scene, such as `o9b2/p3`. */
  id: string;
  /** Other runtime ids that reach this node. A prefab root is also `placement/rootId`. */
  aliases: string[];
  /** The effective entry, with overrides applied. A prefab root takes the root's type. */
  entry: SceneObject;
  /** Project path of the file the entry was defined in. */
  file: string;
  /** The entry's id inside that file. */
  localId: string;
  /** Set on the root of an expanded prefab. */
  placement?: { id: string; prefab: string; file: string };
  /** Resolved children of a container, in draw order. */
  children: ResolvedNode[];
  /** Set when this node could not be built, such as a missing prefab or a cycle. */
  error?: string;
}

export interface ResolveWarning {
  file: string;
  /** Runtime id of the object the warning is about. */
  id: string;
  message: string;
}

export interface ResolveOptions {
  /** Project path of the file the objects come from. */
  file: string;
  getPrefab: PrefabLookup;
  /** Runtime id prefix, for spawning a prefab under a made-up placement id. */
  prefix?: string;
  onWarning?: (w: ResolveWarning) => void;
}

const UNOVERRIDABLE = new Set(['id', 'type', 'children', 'prefab', 'overrides']);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Merges `patch` into a copy of `base`. Objects merge key by key; anything else replaces. */
export function deepMerge<T>(base: T, patch: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(patch)) return structuredClone(patch) as T;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = isPlainObject(v) && isPlainObject(out[k]) ? deepMerge(out[k], v) : structuredClone(v);
  }
  return out as T;
}

/** Overrides waiting to apply inside one prefab scope, keyed by path relative to it. */
type Pending = Map<string, { value: Record<string, unknown>; used: boolean; from: string }[]>;

function addPending(pending: Pending, overrides: PrefabOverrides | undefined, from: string) {
  if (!overrides) return;
  for (const [key, value] of Object.entries(overrides)) {
    if (!isPlainObject(value)) continue;
    const list = pending.get(key) ?? [];
    list.push({ value, used: false, from });
    pending.set(key, list);
  }
}

interface Scope {
  file: string;
  /** Runtime id prefix of this scope, such as `o9b2/`. Empty at the scene's top level. */
  prefix: string;
  pending: Pending;
  /** Prefab files being expanded, outermost first, for cycle detection. */
  stack: string[];
  opts: ResolveOptions;
}

function applyOverrides(entry: SceneObject, scope: Scope, localId: string, runtimeId: string): SceneObject {
  const list = scope.pending.get(localId);
  if (!list) return entry;
  let out = entry;
  for (const item of list) {
    item.used = true;
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(item.value)) {
      if (UNOVERRIDABLE.has(k)) {
        scope.opts.onWarning?.({ file: item.from, id: runtimeId, message: `Override of "${k}" is not allowed and was ignored` });
      } else {
        patch[k] = v;
      }
    }
    out = deepMerge(out, patch);
  }
  return out;
}

function resolveList(objects: readonly SceneObject[] | undefined, scope: Scope): ResolvedNode[] {
  return (objects ?? []).map((o) => resolveOne(o, scope));
}

function resolveOne(obj: SceneObject, scope: Scope): ResolvedNode {
  const runtimeId = scope.prefix + obj.id;
  const entry = applyOverrides(obj, scope, obj.id, runtimeId);
  if (entry.type === 'prefab') return resolvePlacement(entry as PrefabPlacement, scope, runtimeId);

  const node: ResolvedNode = { id: runtimeId, aliases: [], entry, file: scope.file, localId: obj.id, children: [] };
  const children = (entry as { children?: unknown }).children;
  if (Array.isArray(children)) {
    node.children = resolveList(children as SceneObject[], scope);
    node.entry = { ...entry };
    delete (node.entry as { children?: unknown }).children;
  }
  return node;
}

function resolvePlacement(placement: PrefabPlacement, scope: Scope, runtimeId: string): ResolvedNode {
  const failed = (error: string): ResolvedNode => {
    scope.opts.onWarning?.({ file: scope.file, id: runtimeId, message: error });
    return { id: runtimeId, aliases: [], entry: placement, file: scope.file, localId: placement.id, children: [], error };
  };
  const path = placement.prefab;
  if (scope.stack.includes(path)) {
    return failed(`Prefab cycle: ${[...scope.stack, path].join(' → ')}`);
  }
  const prefab = scope.opts.getPrefab(path);
  if (!prefab || !isPlainObject(prefab.root)) return failed(`Prefab "${path}" was not found or has no root`);

  // Overrides for the inner scope: the placement's own first, then any that
  // reach through it from outer scopes (`placementId/innerId`), so outer wins.
  const pending: Pending = new Map();
  addPending(pending, placement.overrides, scope.file);
  const through = placement.id + '/';
  for (const [key, list] of scope.pending) {
    if (!key.startsWith(through)) continue;
    for (const item of list) {
      item.used = true;
      const inner = pending.get(key.slice(through.length)) ?? [];
      inner.push({ ...item, used: false });
      pending.set(key.slice(through.length), inner);
    }
  }

  const inner: Scope = { file: path, prefix: runtimeId + '/', pending, stack: [...scope.stack, path], opts: scope.opts };
  const root = prefab.root;
  let rootEntry = applyOverrides(root, inner, root.id, runtimeId);
  // The placement's transform replaces the root's.
  rootEntry = { ...rootEntry };
  for (const f of PLACEMENT_TRANSFORM_FIELDS) {
    delete (rootEntry as unknown as Record<string, unknown>)[f];
    const v = (placement as unknown as Record<string, unknown>)[f];
    if (v !== undefined) (rootEntry as unknown as Record<string, unknown>)[f] = v;
  }
  if (placement.name !== undefined) rootEntry.name = placement.name;

  let node: ResolvedNode;
  if (rootEntry.type === 'prefab') {
    // A prefab whose root is itself a placement: expand it in place.
    node = resolvePlacement(rootEntry as PrefabPlacement, inner, runtimeId);
    node.aliases.push(joinIdPath(runtimeId, root.id));
  } else {
    node = {
      id: runtimeId,
      aliases: [joinIdPath(runtimeId, root.id)],
      entry: rootEntry,
      file: path,
      localId: root.id,
      children: [],
    };
    const children = (rootEntry as { children?: unknown }).children;
    if (Array.isArray(children)) {
      node.children = resolveList(children as SceneObject[], inner);
      delete (node.entry as { children?: unknown }).children;
    }
  }
  node.placement = { id: placement.id, prefab: path, file: scope.file };

  for (const [key, list] of pending) {
    for (const item of list) {
      if (!item.used) {
        scope.opts.onWarning?.({
          file: item.from,
          id: runtimeId,
          message: `Override "${key}" on prefab placement "${placement.name ?? placement.id}" no longer matches an object in ${path} and was dropped`,
        });
      }
    }
  }
  return node;
}

/** Resolves a scene's objects (or a prefab's root, as a one-item list) into runtime nodes. */
export function resolveObjects(objects: readonly SceneObject[] | undefined, opts: ResolveOptions): ResolvedNode[] {
  const scope: Scope = { file: opts.file, prefix: opts.prefix ?? '', pending: new Map(), stack: [], opts };
  if (opts.file.endsWith('.prefab.json')) scope.stack.push(opts.file);
  return resolveList(objects, scope);
}

/** Expands one prefab as if it were placed with the given placement fields, for runtime spawning. */
export function resolvePrefab(
  placement: Omit<PrefabPlacement, 'type'> & { type?: 'prefab' },
  opts: Omit<ResolveOptions, 'file'> & { file?: string },
): ResolvedNode {
  const nodes = resolveObjects([{ ...placement, type: 'prefab' } as PrefabPlacement], {
    ...opts,
    file: opts.file ?? '<spawn>',
  });
  return nodes[0]!;
}

/** Every prefab file a prefab depends on, directly or through other prefabs. Reports cycles. */
export function findPrefabCycles(getPrefab: PrefabLookup, start: string): string[][] {
  const cycles: string[][] = [];
  const visit = (path: string, stack: string[]) => {
    if (stack.includes(path)) {
      cycles.push([...stack.slice(stack.indexOf(path)), path]);
      return;
    }
    const prefab = getPrefab(path);
    if (!prefab?.root) return;
    const next = [...stack, path];
    const walk = (o: SceneObject) => {
      if (o.type === 'prefab' && typeof (o as PrefabPlacement).prefab === 'string') {
        visit((o as PrefabPlacement).prefab, next);
      }
      const children = (o as { children?: unknown }).children;
      if (Array.isArray(children)) (children as SceneObject[]).forEach(walk);
    };
    walk(prefab.root);
  };
  visit(start, []);
  return cycles;
}
